import { useTranslation } from 'react-i18next';
import { AlignCenterIcon, AlignLeftIcon, AlignRightIcon } from '@/utils/icons';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import type { ITextStyle, TextAlignOption } from '../../../utils/markupTypes';
import styles from './Toolbar.module.css';

const ALIGNMENTS: Array<{ id: TextAlignOption; Icon: (p: { size?: number }) => React.JSX.Element }> = [
  { id: 'left', Icon: AlignLeftIcon },
  { id: 'center', Icon: AlignCenterIcon },
  { id: 'right', Icon: AlignRightIcon },
];

const DIRECTIONS: Array<'ltr' | 'rtl'> = ['ltr', 'rtl'];

interface ITextFormatControlsProps {
  textStyle: ITextStyle;
  disabled: boolean;
  onTextStyleChange: (patch: Partial<ITextStyle>) => void;
}

/** The text formatting row: font size, bold/italic/underline, alignment and
 *  direction. Shows what the next stamp will use — or, while a text stamp is
 *  selected, edits that stamp in place through the same controls. */
export function TextFormatControls({ textStyle, disabled, onTextStyleChange }: ITextFormatControlsProps) {
  const { t } = useTranslation();

  const styleToggle = (key: 'bold' | 'italic' | 'underline', label: string, className: string | undefined) => (
    <Tooltip content={t(`documents.markup.text.${key}`)}>
      <button
        type="button"
        className={`${styles.toolBtn} ${className} ${textStyle[key] ? styles.toolBtnActive : ''}`}
        onClick={() => onTextStyleChange({ [key]: !textStyle[key] })}
        disabled={disabled}
        aria-pressed={textStyle[key]}
        aria-label={t(`documents.markup.text.${key}`)}
      >
        {label}
      </button>
    </Tooltip>
  );

  return (
    <div className={styles.toolGroup}>
      <Tooltip content={t('documents.markup.text.size')}>
        <input
          type="number"
          className={styles.zoomInput}
          value={Math.round(textStyle.size * 1000)}
          min={8}
          max={100}
          onChange={(e) => {
            const v = Math.round(Number(e.target.value));
            if (Number.isFinite(v)) onTextStyleChange({ size: Math.min(100, Math.max(8, v)) / 1000 });
          }}
          disabled={disabled}
          aria-label={t('documents.markup.text.size')}
        />
      </Tooltip>
      {styleToggle('bold', 'B', styles.boldBtn)}
      {styleToggle('italic', 'I', styles.italicBtn)}
      {styleToggle('underline', 'U', styles.underlineBtn)}
      {ALIGNMENTS.map(({ id, Icon }) => (
        <Tooltip key={id} content={t(`documents.markup.text.align.${id}`)}>
          <button
            type="button"
            className={`${styles.toolBtn} ${textStyle.align === id ? styles.toolBtnActive : ''}`}
            onClick={() => onTextStyleChange({ align: id })}
            disabled={disabled}
            aria-pressed={textStyle.align === id}
            aria-label={t(`documents.markup.text.align.${id}`)}
          >
            <Icon size={15} />
          </button>
        </Tooltip>
      ))}
      {DIRECTIONS.map((dir) => (
        <Tooltip key={dir} content={t(`documents.markup.text.direction.${dir}`)}>
          <button
            type="button"
            className={`${styles.toolBtn} ${styles.directionBtn} ${textStyle.direction === dir ? styles.toolBtnActive : ''}`}
            onClick={() => onTextStyleChange({ direction: dir })}
            disabled={disabled}
            aria-pressed={textStyle.direction === dir}
            aria-label={t(`documents.markup.text.direction.${dir}`)}
          >
            {dir.toUpperCase()}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}